import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    RefreshCw, ArrowLeft, Users, Home, Wallet, Landmark, Coins, Percent,
    TrendingUp, TrendingDown, Target, Clock, Shield, AlertTriangle, ChevronDown, ChevronRight,
    Mail, Phone, MapPin, Activity, FileText, Plus, Edit3, Building, Sparkles,
    Eye, Heart, Calculator, Bot, Send, Zap, CheckCircle2, Flame,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchInvestorDetail } from '../../investors/api/investorsApi';

// ── Formatters ──────────────────────────────────────────────────────────────
const fmtCompact = (n) => {
    if (n === null || n === undefined || isNaN(Number(n))) return '—';
    const v = Number(n);
    const sign = v < 0 ? '-' : '';
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
    return `${sign}$${Math.round(abs).toLocaleString()}`;
};
const fmtMoney = (n, signed = false) => {
    if (n === null || n === undefined || isNaN(Number(n))) return '—';
    const v = Math.round(Number(n));
    const sign = signed && v > 0 ? '+' : v < 0 ? '-' : '';
    return `${sign}$${Math.abs(v).toLocaleString()}`;
};
const fmtPct = (n) => (n === null || n === undefined || isNaN(Number(n)) ? 'N/A' : `${Number(n).toFixed(1)}%`);
const fmtNum = (n) => (Number(n) || 0).toLocaleString();
const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) +
        ' · ' + d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
};
const titleCase = (s) => (typeof s === 'string' && s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const GOAL_LABELS = {
    retire: 'Retirement', portfolio: 'Build portfolio', growth: 'Capital growth',
    cashflow: 'Cashflow', first: 'First investment', develop: 'Development',
};
const goalLabel = (g) => GOAL_LABELS[g] || titleCase(g);

const INTENT_TONE = {
    priority: 'bg-red-50 text-red-600', hot: 'bg-orange-50 text-orange-600',
    active: 'bg-amber-50 text-amber-600', warm: 'bg-blue-50 text-blue-600',
    cold: 'bg-gray-100 text-gray-500',
};

const ACTIVITY_ICONS = {
    property_added: Plus, property_updated: Edit3, portfolio_created: FileText,
    loan_updated: Landmark, value_updated: TrendingUp, rate_changed: Percent,
    rent_updated: Coins, fixed_loan_expiring: AlertTriangle,
};
const activityIcon = (type) => ACTIVITY_ICONS[type] || Activity;

const OPP_ICON = {
    finance_review: Landmark, refer_broker: Users, send_stock: Send,
    refer_specialist: Users, review_management: AlertTriangle,
    next_purchase: Home, follow_up: Zap,
};

const Kpi = ({ label, value, sub, tone, Icon, accent = '#2563eb' }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-2">
            {Icon && (
                <span className="flex items-center justify-center w-8 h-8 rounded-lg" style={{ background: `${accent}1a`, color: accent }}>
                    <Icon className="w-4 h-4" />
                </span>
            )}
            <span className="text-xs font-medium text-gray-500">{label}</span>
        </div>
        <div className={`text-2xl font-bold ${tone === 'g' ? 'text-emerald-600' : tone === 'r' ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
        {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
);

const Stat = ({ label, value, Icon, accent = '#2563eb' }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
        <span className="flex items-center justify-center w-9 h-9 rounded-lg shrink-0" style={{ background: `${accent}1a`, color: accent }}>
            <Icon className="w-4 h-4" />
        </span>
        <div className="min-w-0">
            <div className="text-lg font-bold text-gray-900 leading-tight">{value}</div>
            <div className="text-xs text-gray-400">{label}</div>
        </div>
    </div>
);

// ── Portfolio card (Tab B) ────────────────────────────────────────────────
const PortfolioCard = ({ portfolio }) => {
    const [open, setOpen] = useState(true);
    const props = portfolio.properties || [];
    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-gray-50 transition-colors text-left">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{portfolio.name || 'Unfiled'}</span>
                        {portfolio.entity_type_display && portfolio.entity_type_display !== 'Unfiled' && (
                            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{portfolio.entity_type_display}</span>
                        )}
                        <span className="text-xs text-gray-400">{fmtNum(portfolio.property_count)} propert{Number(portfolio.property_count) === 1 ? 'y' : 'ies'}</span>
                    </div>
                    {portfolio.notes && <p className="text-xs text-gray-400 mt-1 truncate">{portfolio.notes}</p>}
                </div>
                <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right hidden sm:block">
                        <div className="font-bold text-gray-900">{fmtCompact(portfolio.portfolio_value)}</div>
                        <div className="text-[11px] text-gray-400">value · {fmtCompact(portfolio.total_equity)} equity</div>
                    </div>
                    {open ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                </div>
            </button>
            {open && (
                <div className="border-t border-gray-100">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-100 text-center">
                        {[['Debt', fmtCompact(portfolio.total_debt), 'text-gray-900'],
                          ['Accessible', fmtCompact(portfolio.accessible_equity), 'text-emerald-600'],
                          ['Avg LVR', fmtPct(portfolio.avg_lvr), 'text-gray-900'],
                          ['Net yield', fmtPct(portfolio.avg_net_yield), 'text-gray-900']].map(([l, v, c]) => (
                            <div key={l} className="bg-white px-3 py-2">
                                <div className="text-[11px] text-gray-400">{l}</div>
                                <div className={`text-sm font-semibold ${c}`}>{v}</div>
                            </div>
                        ))}
                    </div>
                    {props.length === 0 ? (
                        <p className="text-xs text-gray-400 italic px-5 py-4">No properties in this portfolio.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-100 bg-gray-50">
                                        <th className="px-4 py-2.5">Property</th>
                                        <th className="px-4 py-2.5 text-right">Value</th>
                                        <th className="px-4 py-2.5 text-right">Equity</th>
                                        <th className="px-4 py-2.5 text-right">LVR</th>
                                        <th className="px-4 py-2.5 text-right">Net yield</th>
                                        <th className="px-4 py-2.5 text-right">Cashflow / mo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {props.map((p) => {
                                        const cf = Number(p.monthly_cashflow);
                                        const cfTone = isNaN(cf) ? 'text-gray-700' : cf >= 0 ? 'text-emerald-600' : 'text-red-600';
                                        const loc = [p.suburb, p.state].filter(Boolean).join(', ');
                                        return (
                                            <tr key={p.id} className="border-b border-gray-50 last:border-0">
                                                <td className="px-4 py-3">
                                                    <div className="font-medium text-gray-900 flex items-center gap-1.5">
                                                        <MapPin className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                                                        <span className="truncate">{p.street_address || 'Address unknown'}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-400 ml-5">{loc || titleCase(p.property_type) || '—'}</div>
                                                </td>
                                                <td className="px-4 py-3 text-right tabular-nums text-gray-900">{fmtCompact(p.current_value)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums text-gray-900">{fmtCompact(p.equity)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums text-gray-700">{fmtPct(p.lvr)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums text-gray-700">{fmtPct(p.net_yield)}</td>
                                                <td className={`px-4 py-3 text-right tabular-nums font-medium ${cfTone}`}>{fmtMoney(p.monthly_cashflow, true)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Property detail (Tab C) ─────────────────────────────────────────────────
const field = (label, value) => (
    <div>
        <div className="text-[11px] text-gray-400">{label}</div>
        <div className="text-sm font-medium text-gray-800">{value}</div>
    </div>
);

const PropertyDetailCard = ({ p }) => {
    const [open, setOpen] = useState(false);
    const cf = Number(p.monthly_cashflow);
    const cfTone = isNaN(cf) ? 'text-gray-700' : cf >= 0 ? 'text-emerald-600' : 'text-red-600';
    const loc = [p.suburb, p.state, p.postcode].filter(Boolean).join(' ');
    const loan = p.loan || null;
    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-gray-50 text-left">
                <div className="min-w-0 flex items-center gap-3">
                    <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 text-[#2563eb] shrink-0"><Home className="w-4 h-4" /></span>
                    <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{p.street_address || 'Address unknown'}</div>
                        <div className="text-xs text-gray-400 truncate">{loc || '—'} · {titleCase(p.property_type) || 'Property'}{p.ownership_entity ? ` · ${titleCase(p.ownership_entity)}` : ''}</div>
                    </div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right hidden sm:block">
                        <div className="font-bold text-gray-900">{fmtCompact(p.current_value)}</div>
                        <div className={`text-[11px] ${cfTone}`}>{fmtMoney(p.monthly_cashflow, true)}/mo</div>
                    </div>
                    {open ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                </div>
            </button>
            {open && (
                <div className="border-t border-gray-100 p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {field('Current value', fmtMoney(p.current_value))}
                    {field('Purchase price', fmtMoney(p.purchase_price))}
                    {field('Equity', fmtMoney(p.equity))}
                    {field('Loan amount', fmtMoney(p.loan_balance))}
                    {field('LVR', fmtPct(p.lvr))}
                    {field('Interest rate', loan?.interest_rate != null ? fmtPct(loan.interest_rate) : '—')}
                    {field('Loan type', loan ? `${titleCase(loan.rate_type) || ''}${loan.repayment_type ? ` · ${loan.repayment_type.toUpperCase()}` : ''}` : 'Outright')}
                    {field('Fixed expiry', loan?.fixed_expiry_date ? fmtDate(loan.fixed_expiry_date) : '—')}
                    {field('Weekly rent', p.weekly_rent ? fmtMoney(p.weekly_rent) : '—')}
                    {field('Annual expenses', fmtMoney(p.annual_expenses))}
                    {field('Gross yield', fmtPct(p.gross_yield))}
                    {field('Net yield', fmtPct(p.net_yield))}
                    {field('Monthly cashflow', fmtMoney(p.monthly_cashflow, true))}
                    {field('Annual cashflow', fmtMoney(p.annual_cashflow, true))}
                    {field('Management', p.is_managed ? (p.property_manager || 'Managed') : 'Self-managed')}
                    {field('Tenant status', titleCase(p.tenant_status) || '—')}
                </div>
            )}
        </div>
    );
};

// ── Opportunity card (Tab E) ────────────────────────────────────────────────
const OpportunityCard = ({ opp, investor }) => {
    const Icon = OPP_ICON[opp.action_type] || Sparkles;
    const toneRing = opp.tone === 'risk' ? 'border-l-red-400' : opp.tone === 'intent' ? 'border-l-orange-400' : 'border-l-emerald-400';
    const act = () => {
        if (opp.action_type === 'send_stock' && investor.email) {
            window.location.href = `mailto:${investor.email}?subject=${encodeURIComponent('Investment opportunities for you')}`;
        } else if (investor.phone) {
            toast.success(`Suggested: ${opp.suggested_action} — logged for ${investor.name}`);
        } else {
            toast.success(`Suggested: ${opp.suggested_action}`);
        }
    };
    return (
        <div className={`bg-white rounded-xl border border-gray-200 border-l-4 ${toneRing} p-5`}>
            <div className="flex items-start gap-3">
                <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-50 text-gray-600 shrink-0"><Icon className="w-4 h-4" /></span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                        <h4 className="text-sm font-semibold text-gray-900">{opp.title}</h4>
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 shrink-0">{opp.confidence}% confidence</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{opp.reason}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                        <button onClick={act} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-[#2563eb] hover:bg-blue-700">
                            <Send className="w-3.5 h-3.5" /> {opp.suggested_action}
                        </button>
                        <button onClick={() => toast.success('Follow-up flagged (session only until Tasks backend ships)')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50">
                            <Zap className="w-3.5 h-3.5" /> Assign follow-up
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = [
    { key: 'overview', label: 'Overview', Icon: Sparkles },
    { key: 'portfolios', label: 'Portfolios', Icon: Building },
    { key: 'properties', label: 'Properties', Icon: Home },
    { key: 'activity', label: 'Activity', Icon: Activity },
    { key: 'opportunities', label: 'Opportunities', Icon: Zap },
    { key: 'timeline', label: 'Timeline', Icon: Clock },
    { key: 'notes', label: 'Notes / Tasks', Icon: FileText },
    { key: 'advice', label: 'GeeVee Advice', Icon: Bot },
];

const InvestorProfilePanel = ({ investorId, onBack }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [tab, setTab] = useState('overview');
    const [notes, setNotes] = useState([]);
    const [noteText, setNoteText] = useState('');
    const tokenRef = useRef(0);

    const load = useCallback(() => {
        const myToken = ++tokenRef.current;
        setLoading(true);
        setError(false);
        fetchInvestorDetail(investorId)
            .then((d) => { if (myToken === tokenRef.current) setData(d); })
            .catch((err) => { if (myToken === tokenRef.current) { console.error(err); setError(true); } })
            .finally(() => { if (myToken === tokenRef.current) setLoading(false); });
    }, [investorId]);

    useEffect(() => { load(); }, [load]);

    // Opening a profile from the (scrolled-down) investor table would otherwise
    // keep the window's scroll offset and land mid-page on the overview tiles —
    // jump back to the top so the investor's name/header is what you see first.
    useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, [investorId]);

    const BackLink = () => (
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to investors
        </button>
    );

    if (loading && !data) {
        return (
            <div className="space-y-6">
                <BackLink />
                <div className="flex items-center justify-center py-20 text-gray-500"><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading investor…</div>
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="space-y-6">
                <BackLink />
                <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                    <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700">Couldn't load this investor</p>
                    <button onClick={load} className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-[#2563eb] hover:bg-blue-700"><RefreshCw className="w-4 h-4" /> Retry</button>
                </div>
            </div>
        );
    }

    const investor = data.investor || {};
    const goalsBlock = investor.goals || {};
    const goals = goalsBlock.goals || [];
    const t = data.totals || {};
    const portfolios = data.portfolios || [];
    const activity = data.activity || [];
    const behaviour = data.behaviour || {};
    const intent = behaviour.intent || {};
    const opportunities = data.opportunities || [];
    const advice = data.geevee_advice || '';
    const allProps = portfolios.flatMap((p) => p.properties || []);
    const trend = intent.score_trend || 0;

    const addNote = () => {
        const txt = noteText.trim();
        if (!txt) return;
        setNotes((n) => [{ id: Date.now(), text: txt, at: new Date().toISOString() }, ...n]);
        setNoteText('');
        toast.success('Note added (session only until Notes backend ships)');
    };

    return (
        <div className="space-y-6">
            <BackLink />

            {/* Header */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-start gap-4 flex-wrap">
                    <span className="flex items-center justify-center w-12 h-12 rounded-xl shrink-0" style={{ background: '#2563eb1a', color: '#2563eb' }}><Users className="w-6 h-6" /></span>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-xl font-bold text-gray-900">{investor.name || 'Unnamed investor'}</h1>
                            {intent.intent_category && (
                                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${INTENT_TONE[intent.intent_category] || 'bg-gray-100 text-gray-500'}`}>
                                    {titleCase(intent.intent_category)}{intent.buying_score != null ? ` · ${intent.buying_score}` : ''}
                                </span>
                            )}
                            {intent.likely_transaction && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600"><Flame className="w-3 h-3" /> Likely 90-day buyer</span>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                            {investor.email && <a href={`mailto:${investor.email}`} className="inline-flex items-center gap-1.5 hover:text-[#2563eb]"><Mail className="w-3.5 h-3.5" /> {investor.email}</a>}
                            {investor.phone && <a href={`tel:${investor.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#2563eb]"><Phone className="w-3.5 h-3.5" /> {investor.phone}</a>}
                            {investor.created_at && <span className="text-xs text-gray-400">Joined {fmtDate(investor.created_at)}</span>}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                            {goals.length > 0 ? goals.map((g) => (
                                <span key={g} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-pink-50 text-pink-600"><Target className="w-3 h-3" /> {goalLabel(g)}</span>
                            )) : <span className="text-xs text-gray-400 italic">No investment goals set</span>}
                            {goalsBlock.risk_appetite && <span className="inline-flex items-center gap-1.5 text-xs text-gray-500"><Shield className="w-3.5 h-3.5 text-amber-500" /> {titleCase(goalsBlock.risk_appetite)} risk</span>}
                            {goalsBlock.time_horizon && <span className="inline-flex items-center gap-1.5 text-xs text-gray-500"><Clock className="w-3.5 h-3.5 text-cyan-500" /> {titleCase(goalsBlock.time_horizon)}</span>}
                        </div>
                    </div>
                    {/* Quick actions */}
                    <div className="flex items-center gap-2">
                        {investor.phone && <a href={`tel:${investor.phone}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50"><Phone className="w-3.5 h-3.5" /> Call</a>}
                        {investor.email && <a href={`mailto:${investor.email}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white rounded-lg bg-[#2563eb] hover:bg-blue-700"><Mail className="w-3.5 h-3.5" /> Email</a>}
                    </div>
                </div>
            </div>

            {/* Headline KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi label="Portfolio value" value={fmtCompact(t.portfolio_value)} sub={`${fmtNum(t.property_count)} properties`} Icon={Home} accent="#2563eb" />
                <Kpi label="Total equity" value={fmtCompact(t.total_equity)} sub={t.avg_lvr != null ? `Avg LVR ${fmtPct(t.avg_lvr)}` : null} Icon={Wallet} accent="#0e9f6e" />
                <Kpi label="Accessible equity" tone="g" value={fmtCompact(t.accessible_equity)} sub="at 80% LVR" Icon={TrendingUp} accent="#0e9f6e" />
                <Kpi label="Monthly cashflow" tone={Number(t.monthly_cashflow) >= 0 ? 'g' : 'r'} value={fmtMoney(t.monthly_cashflow, true)} sub={`Net yield ${fmtPct(t.avg_net_yield)}`} Icon={Coins} accent="#d97706" />
            </div>

            {/* Tab strip */}
            <div className="flex bg-gray-100 p-1 rounded-xl w-full overflow-x-auto">
                {TABS.map(({ key, label, Icon }) => (
                    <button key={key} onClick={() => setTab(key)}
                        className={`flex flex-shrink-0 items-center justify-center gap-2 px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition-all whitespace-nowrap ${tab === key ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {label}
                        {key === 'opportunities' && opportunities.length > 0 && (
                            <span className="ml-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600">{opportunities.length}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* ── A. Overview ── */}
            {tab === 'overview' && (
                <div className="space-y-4">
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-2"><Bot className="w-4 h-4 text-[#2563eb]" /> GeeVee summary</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{advice}</p>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <Kpi label="Buying score" value={intent.buying_score != null ? `${intent.buying_score}/100` : '—'} sub={intent.intent_category ? titleCase(intent.intent_category) : 'Not scored'} Icon={Flame} accent="#dc2626" />
                        <Kpi label="Finance readiness" value={intent.finance_label || '—'} sub={intent.timeline_label ? `Timeline ${intent.timeline_label}` : null} Icon={Landmark} accent="#0891b2" />
                        <Kpi label="Score trend" tone={trend > 0 ? 'g' : trend < 0 ? 'r' : undefined} value={`${trend > 0 ? '+' : ''}${trend}`} sub="vs last week" Icon={trend >= 0 ? TrendingUp : TrendingDown} accent="#7c3aed" />
                        <Kpi label="Open opportunities" value={fmtNum(opportunities.length)} sub={opportunities[0] ? opportunities[0].title : 'None right now'} Icon={Zap} accent="#d97706" />
                    </div>
                    {opportunities[0] && (
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2"><Zap className="w-4 h-4 text-orange-500" /> Next best action</h3>
                            <OpportunityCard opp={opportunities[0]} investor={investor} />
                        </div>
                    )}
                </div>
            )}

            {/* ── B. Portfolios ── */}
            {tab === 'portfolios' && (
                <div className="space-y-4">
                    {portfolios.length === 0 ? (
                        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center"><Building className="w-7 h-7 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-500">No portfolios tracked yet.</p></div>
                    ) : portfolios.map((p) => <PortfolioCard key={p.id ?? 'unfiled'} portfolio={p} />)}
                </div>
            )}

            {/* ── C. Properties ── */}
            {tab === 'properties' && (
                <div className="space-y-3">
                    {allProps.length === 0 ? (
                        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center"><Home className="w-7 h-7 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-500">No properties tracked yet.</p></div>
                    ) : allProps.map((p) => <PropertyDetailCard key={p.id} p={p} />)}
                </div>
            )}

            {/* ── D. Activity ── */}
            {tab === 'activity' && (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        <Stat label="Properties viewed" value={fmtNum(behaviour.properties_viewed)} Icon={Eye} accent="#2563eb" />
                        <Stat label="Properties saved" value={fmtNum(behaviour.properties_saved)} Icon={Heart} accent="#db2777" />
                        <Stat label="Enquiries sent" value={fmtNum(behaviour.enquiries_sent)} Icon={Mail} accent="#0e9f6e" />
                        <Stat label="Offers made" value={fmtNum(behaviour.offers_made)} Icon={FileText} accent="#7c3aed" />
                        <Stat label="Investment calc" value={fmtNum(behaviour.investment_calc_uses)} Icon={Calculator} accent="#d97706" />
                        <Stat label="Mortgage calc" value={fmtNum(behaviour.mortgage_calc_uses)} Icon={Calculator} accent="#0891b2" />
                        <Stat label="GeeVee questions" value={fmtNum(behaviour.geevee_questions)} Icon={Bot} accent="#2563eb" />
                        <Stat label="Inspections" value={fmtNum(behaviour.inspections_booked)} Icon={CheckCircle2} accent="#0e9f6e" />
                        <Stat label="Call clicks" value={fmtNum(behaviour.calls_clicked)} Icon={Phone} accent="#0891b2" />
                        <Stat label="Email clicks" value={fmtNum(behaviour.emails_clicked)} Icon={Mail} accent="#7c3aed" />
                        <Stat label="Finance/broker clicks" value={fmtNum(behaviour.broker_clicks)} Icon={Landmark} accent="#dc2626" />
                        <Stat label="Last login" value={fmtDate(behaviour.last_login)} Icon={Clock} accent="#64748b" />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-[#2563eb]" /> Suburbs searched</h3>
                            {(behaviour.suburbs_searched || []).length === 0 ? <p className="text-xs text-gray-400 italic">No suburb activity yet.</p> : (
                                <div className="flex flex-wrap gap-2">
                                    {behaviour.suburbs_searched.map((s, i) => (
                                        <span key={i} className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-600">{s.suburb}{s.state ? `, ${s.state}` : ''}{s.event_count ? ` · ${s.event_count}` : ''}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><Home className="w-4 h-4 text-[#0e9f6e]" /> Property types viewed</h3>
                            {(behaviour.property_types_viewed || []).length === 0 ? <p className="text-xs text-gray-400 italic">No view activity yet.</p> : (
                                <div className="flex flex-wrap gap-2">
                                    {behaviour.property_types_viewed.map((pt, i) => (
                                        <span key={i} className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600">{titleCase(pt.type)} · {pt.share}%</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── E. Opportunities ── */}
            {tab === 'opportunities' && (
                <div className="space-y-3">
                    {opportunities.length === 0 ? (
                        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center"><Zap className="w-7 h-7 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-500">No opportunities right now.</p><p className="text-xs text-gray-400 mt-1">GeeVee surfaces these from equity, cashflow, loans and behaviour.</p></div>
                    ) : opportunities.map((o) => <OpportunityCard key={o.key} opp={o} investor={investor} />)}
                </div>
            )}

            {/* ── F. Timeline ── */}
            {tab === 'timeline' && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    {activity.length === 0 ? <p className="text-xs text-gray-400 italic">No recorded activity.</p> : (
                        <ol className="relative border-l border-gray-100 ml-2 space-y-5">
                            {activity.map((ev) => {
                                const Icon = activityIcon(ev.event_type);
                                return (
                                    <li key={ev.id} className="ml-5">
                                        <span className="absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full bg-blue-50 text-[#2563eb] ring-4 ring-white"><Icon className="w-3.5 h-3.5" /></span>
                                        <div className="text-sm font-medium text-gray-900">{ev.summary || ev.event_type_display || 'Activity'}</div>
                                        <div className="text-[11px] text-gray-400 mt-1">
                                            {[ev.portfolio_name, ev.property_address].filter(Boolean).join(' · ')}
                                            {(ev.portfolio_name || ev.property_address) ? ' · ' : ''}
                                            {fmtDateTime(ev.occurred_at)}
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
            )}

            {/* ── G. Notes / Tasks ── */}
            {tab === 'notes' && (
                <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-700">
                        Notes are session-only for now — persistence needs an investor Notes/Tasks backend model (next step).
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} placeholder="Add a note — call outcome, next step, context…"
                            className="w-full text-sm border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]" />
                        <div className="flex justify-end mt-2">
                            <button onClick={addNote} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg bg-[#2563eb] hover:bg-blue-700"><Plus className="w-4 h-4" /> Add note</button>
                        </div>
                    </div>
                    {notes.length > 0 && (
                        <div className="space-y-2">
                            {notes.map((n) => (
                                <div key={n.id} className="bg-white rounded-xl border border-gray-200 p-4">
                                    <p className="text-sm text-gray-700">{n.text}</p>
                                    <div className="text-[11px] text-gray-400 mt-1">{fmtDateTime(n.at)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── H. GeeVee Advice ── */}
            {tab === 'advice' && (
                <div className="space-y-4">
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-2"><Bot className="w-4 h-4 text-[#2563eb]" /> GeeVee advice for staff</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{advice}</p>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <Kpi label="Buying score" value={intent.buying_score != null ? `${intent.buying_score}/100` : '—'} sub={intent.intent_category ? titleCase(intent.intent_category) : null} Icon={Flame} accent="#dc2626" />
                        <Kpi label="Finance" value={intent.finance_label || '—'} Icon={Landmark} accent="#0891b2" />
                        <Kpi label="Investor lean" value={intent.investor_lean != null ? `${Math.round(intent.investor_lean * 100)}%` : '—'} sub="investor vs owner-occ" Icon={Target} accent="#7c3aed" />
                        <Kpi label="Timeline" value={intent.timeline_label || '—'} Icon={Clock} accent="#0e9f6e" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvestorProfilePanel;
